/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_RATINGS_REVIEWS */

/**
 * Pure helpers that port the Bazaarvoice LINK cartridge's product-id and
 * loader-url logic (SFRA `libBazaarvoice.js`) to the SFNext storefront.
 *
 * Kept side-effect free so it is trivially unit-testable and safe to import on
 * both server and client.
 */

/**
 * Escape characters Bazaarvoice cannot accept in a product id, matching SFRA
 * `libBazaarvoice.replaceIllegalCharacters`: `&` -> `_and_`, `/` -> `_fslash_`,
 * space -> `-`. Applied to every id before it is placed in a `data-bv-product-id`.
 */
export function replaceIllegalCharacters(id: string): string {
    return id.replaceAll('&', '_and_').replaceAll('/', '_fslash_').replaceAll(' ', '-');
}

export interface BvProductIdOptions {
    /** SFRA `bvEnableProductPrefix`: prepend `<siteId>-` to the id. */
    productPrefixEnabled?: boolean;
    /** Site id used for the prefix when `productPrefixEnabled` is true. */
    siteId?: string;
}

/**
 * Build the Bazaarvoice product id for a given SFCC product id.
 *
 * Master-vs-variant selection (SFRA `bvEntityId`) is resolved upstream by the
 * PDP loader's `productLookupId`, so this helper only applies the two id
 * transforms that always run at display time: illegal-character escaping and
 * the optional site-id prefix.
 */
export function getBvProductId(productId: string, options: BvProductIdOptions = {}): string {
    const escaped = replaceIllegalCharacters(productId);
    if (options.productPrefixEnabled && options.siteId) {
        return `${options.siteId}-${escaped}`;
    }
    return escaped;
}

export interface BvLoaderUrlOptions {
    /** BV client name (SFRA `bvCustomerName`). */
    customerName: string;
    /** BV deployment zone (SFRA `bvDeploymentZone`), e.g. `Main Site` / `baggallini`. */
    deploymentZone: string;
    /** BV environment (SFRA `bvEnvironment`): `staging` | `production` (case-insensitive). */
    environment: string;
    /** BV display locale (from SFRA `bvLocaleMapping`), e.g. `en_US`. */
    locale: string;
    /** Loader host; defaults to the SFRA constant `apps.bazaarvoice.com`. */
    loaderHost?: string;
}

const DEFAULT_LOADER_HOST = 'apps.bazaarvoice.com';

/**
 * Build the `bv.js` loader URL, matching SFRA `libBazaarvoice.getBvLoaderUrl`:
 *   https://<host>/deployments/<customerName>/<zoneId>/<env>/<locale>/bv.js
 *
 * The deployment zone is lowercased with spaces replaced by `_`, and the
 * environment is lowercased. The host is the same for staging and production.
 * Returns `null` when a required segment is missing so callers can no-op safely.
 *
 * Emitted with an explicit `https://` scheme rather than the protocol-relative
 * `//host` form SFRA used: Bazaarvoice serves the loader over https only (an
 * `http://` request 301-redirects, which browsers refuse to follow for a
 * `<script>`), and the CSP allows only the `https://` origin. Protocol-relative
 * would resolve to `http://` on a local `http://localhost` dev server and be
 * blocked on both counts.
 */
export function getBvLoaderUrl(options: BvLoaderUrlOptions): string | null {
    const { customerName, deploymentZone, environment, locale } = options;
    if (!customerName || !deploymentZone || !environment || !locale) {
        return null;
    }
    const host = options.loaderHost || DEFAULT_LOADER_HOST;
    const zoneId = deploymentZone.toLowerCase().replaceAll(' ', '_');
    const env = environment.toLowerCase();
    // Encode the operator-supplied segments so a future config source can't break
    // the URL. Current values (`rgbarry`, `en_US`) are unaffected by encoding.
    return `https://${host}/deployments/${encodeURIComponent(customerName)}/${zoneId}/${env}/${encodeURIComponent(locale)}/bv.js`;
}
